/**
 * Bottom-sheet OTP for `AuthSheetHost`.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuthNavigation } from '../shared/navigation/useAuthNavigation';
import { View, Text, Alert, ScrollView } from 'react-native';
import { Clickable2 } from '../components/common/Clickable2';
import { ButtonLoader } from '../components/load-main/ButtonLoader';
import { SvgIcon } from '../components/common/SvgIcon';
import { OTPInput } from '../components/common/Input';

import { appTheme, useThemeColors } from '../shared/theme';
import { postVerifyOtp, requestOtpResendGet, setCredentials, resolveOtpDismissPolicy, confirmBlockingOtpAbort } from '../shared/auth';
import { useAuthStore } from '../shared/auth/store';
import { useTranslation } from '../shared/i18n';
import { login as authLogin } from '../shared/auth/authSessionApi';
import { fetchProfileAndSyncUser } from '../data/domains/profile';
import { getSelectedSubscription } from '../data/domains/subscription';
import {
  sharedStyles,
  buttonWrap,
  createFrostedPrimaryGlassLabelStyle,
  createFrostedPrimaryGlassSurfaceStyle,
} from '../shared/theme/sharedStyles';
import { createOtpCodeEntrySheetStyles } from '../shared/theme/authSheetStyles';
import { authSheetFormSpacing, AUTH_FORM_KEYBOARD, useAuthFormScrollMaxHeight } from '../shared/auth';
import { useAuthInputKeyboardAvoidance } from './keyboard/useAuthInputKeyboardAvoidance';
import { AuthFocusTarget } from './keyboard/AuthFocusTarget';

export type OTPVerificationSheetContentProps = {
  onDismiss: () => void;
  routeParams: Record<string, unknown>;
  onAbortSafeExit?: () => void;
  /** Extra auth modal lift when scroll cannot clear the focused field (overlap only, never full keyboard height). */
  onKeyboardPanelNudge?: (offset_px: number) => void;
};

export function OTPVerificationSheetContent({ onDismiss, routeParams: params, onAbortSafeExit, onKeyboardPanelNudge }: OTPVerificationSheetContentProps) {
  const navigation = useAuthNavigation();
  const { t } = useTranslation();
  const user = useAuthStore(s => s.user);

  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createOtpCodeEntrySheetStyles(colors), [colors]);
  const scroll_max_h = useAuthFormScrollMaxHeight();

  const phone = (params.phone as string) || '';
  const countryCode = (params.countryCode as string) || '+965';
  const context = (params.context as string) || 'signin';
  const redirectTo = (params.redirectTo as string) || 'MainTabs';
  const loginData = params.loginData as { phone?: string; password?: string } | undefined;
  const checkoutPayload = params.checkoutPayload;
  const signupPassword = params.signupPassword as string | undefined;
  const requiredVerification = params.requiredVerification === true;
  const show_blocking_abort = resolveOtpDismissPolicy(params) === 'blocking' && onAbortSafeExit != null;

  const [otpString, setOtpString] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpStatus, setOtpStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [timer, setTimer] = useState(36);
  const [canResend, setCanResend] = useState(false);

  const sentOtpOnMountRef = useRef(false);

  /** Register already triggers OTP SMS; only auto-request here for non-signup flows (e.g. forced verification). `skipInitialResend` overrides. */
  const skip_initial_otp_request =
    params.skipInitialResend === true || ((params.context as string) || 'signin') === 'signup';

  useEffect(() => {
    if (!user?.id || sentOtpOnMountRef.current) {
      return;
    }
    sentOtpOnMountRef.current = true;
    if (skip_initial_otp_request) {
      return;
    }
    (async () => {
      try {
        await requestOtpResendGet(user.id);
      } catch {
        // ignore; user can use Resend
      }
    })();
  }, [user?.id, skip_initial_otp_request]);

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setCanResend(true);
    }
  }, [timer]);

  const getOTP = async () => {
    if (!user?.id) {
      return;
    }

    try {
      const response: { message?: string } = (await requestOtpResendGet(user.id)) as { message?: string };
      if (response.message) {
        Alert.alert(t('auth.otpSent'), response.message);
      }
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : t('auth.failedToSendOtp');
      Alert.alert(t('common.error'), msg);
    }
  };

  const resendOTP = () => {
    setCanResend(false);
    setTimer(36);
    getOTP();
  };

  const verifyOTP = async () => {
    if (otpString.length !== 6) {
      Alert.alert(t('common.error'), t('auth.enterCompleteOtp'));
      return;
    }

    if (!user?.id) {
      Alert.alert(t('common.error'), t('auth.userNotFound'));
      return;
    }

    setIsLoading(true);
    setOtpStatus('pending');

    try {
      const response: { success?: boolean; message?: string } = (await postVerifyOtp({
        phone: phone,
        country_iso_code: countryCode,
        customer_id: user.id,
        otp: otpString,
      })) as { success?: boolean; message?: string };

      if (response.success) {
        setOtpStatus('success');

        if (context === 'signup' && typeof signupPassword === 'string' && signupPassword.length > 0) {
          const loginOk = await authLogin(phone, signupPassword);
          if (!loginOk) {
            setOtpStatus('error');
            Alert.alert(t('common.error'), t('auth.loginFailed'));
            return;
          }
        } else if (user?.id) {
          await fetchProfileAndSyncUser(user.id);
        }

        Alert.alert(t('common.success'), response.message || t('auth.otpVerifiedSuccess'), [
          {
            text: 'OK',
            onPress: () => {
              void handleOtpSuccess();
            },
          },
        ]);
      } else {
        setOtpStatus('error');
        Alert.alert(t('common.error'), response.message || t('auth.invalidOtp'));
      }
    } catch (error: unknown) {
      setOtpStatus('error');
      const msg = error instanceof Error ? error.message : t('auth.failedToVerifyOtp');
      Alert.alert(t('common.error'), msg);
    } finally {
      setIsLoading(false);
    }
  };

  const navigateAfterSignin = async () => {
    onDismiss();
    if (redirectTo === 'AddressForm') {
      navigation.navigate('AddressForm', {
        afterSaveGoTo: 'Checkout',
      });
      return;
    }
    if (redirectTo === 'Checkout') {
      const payload =
        (checkoutPayload && typeof checkoutPayload === 'object' ? (checkoutPayload as object) : null) ??
        ((await getSelectedSubscription()) as object | null);
      navigation.reset({
        index: 0,
        routes: [
          {
            name: 'Checkout' as never,
            params: (payload && typeof payload === 'object' ? { data: payload } : undefined) as never,
          },
        ],
      });
      return;
    }
    navigation.reset({
      index: 0,
      routes: [{ name: redirectTo as never }],
    });
  };

  const handleOtpSuccess = async () => {
    if (requiredVerification) {
      onDismiss();
      return;
    }
    if (context === 'signin') {
      if (loginData && typeof loginData.phone === 'string' && typeof loginData.password === 'string') {
        try {
          await setCredentials(String(loginData.phone), String(loginData.password));
        } catch {
          // ignore
        }
      }
      void navigateAfterSignin();
    } else if (context === 'signup') {
      if (typeof signupPassword === 'string' && signupPassword.length > 0 && phone.length > 0) {
        try {
          await setCredentials(phone, signupPassword);
        } catch {
          // ignore
        }
      }
      const payload = (checkoutPayload as object | undefined) ?? (await getSelectedSubscription());
      onDismiss();
      if (redirectTo === 'AddressForm') {
        navigation.navigate('AddressForm', {
          afterSaveGoTo: payload ? 'Checkout' : undefined,
        });
      } else if (payload) {
        navigation.navigate('Checkout', { data: payload });
      } else {
        navigation.navigate('SubscriptionList');
      }
    } else {
      onDismiss();
      if (typeof redirectTo === 'string' && redirectTo.length > 0) {
        navigation.navigate(redirectTo);
      }
    }
  };

  const isOtpComplete = otpString.length === 6;

  const scroll_ref = useRef<ScrollView>(null);
  const scroll_y = useRef(0);
  const otp_block_ref = useRef<View>(null);
  const { on_field_focus, on_scroll_layout, on_scroll_content_size_change, keyboard_scroll_pad } = useAuthInputKeyboardAvoidance({
    enabled: true,
    scroll_ref,
    scroll_y_ref: scroll_y,
    on_panel_offset: onKeyboardPanelNudge,
    clearance_px: AUTH_FORM_KEYBOARD.fieldFocusClearancePx,
  });

  const frosted_primary = useMemo(
    () => ({
      surface: createFrostedPrimaryGlassSurfaceStyle(colors),
      label: createFrostedPrimaryGlassLabelStyle(colors),
    }),
    [colors],
  );

  return (
    <View style={styles.keyboardView}>
      <ScrollView
        ref={scroll_ref}
        onScroll={e => {
          scroll_y.current = e.nativeEvent.contentOffset.y;
        }}
        onLayout={on_scroll_layout}
        onContentSizeChange={on_scroll_content_size_change}
        scrollEventThrottle={16}
        style={[styles.scroll, { maxHeight: scroll_max_h }]}
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingTop: authSheetFormSpacing.formPaddingTop,
            paddingBottom: authSheetFormSpacing.formPaddingBottom + keyboard_scroll_pad,
          },
        ]}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="interactive"
        showsVerticalScrollIndicator={false}
        automaticallyAdjustKeyboardInsets={false}
        bounces={false}
      >
        <View style={styles.stepsContainer}>
          <View style={styles.stepCircle}>
            <Text style={styles.stepText}>1</Text>
          </View>
          <View style={styles.stepLine} />
          <View style={[styles.stepCircle, styles.stepCircleActive]}>
            <Text style={[styles.stepText, styles.stepTextActive]}>2</Text>
          </View>
        </View>

        <Text style={styles.title}>{t('auth.otpVerification')}</Text>

        {otpStatus === 'error' ? (
          <View style={styles.statusContainer}>
            <SvgIcon name="x" size={60} color={colors.error} />
          </View>
        ) : null}

        {otpStatus === 'success' ? (
          <View style={styles.statusContainer}>
            <SvgIcon name="check" size={60} color={colors.success} />
          </View>
        ) : null}

        <Text style={styles.subtitle}>
          {t('auth.otpSentTo')}
          {'\n'}
          <Text style={styles.phoneText}>
            {countryCode} {phone}
          </Text>
        </Text>

        <AuthFocusTarget ref={otp_block_ref}>
          <OTPInput value={otpString} onChangeText={setOtpString} onFocus={() => on_field_focus(otp_block_ref)} error={otpStatus === 'error'} />
        </AuthFocusTarget>

        {canResend ? (
          <View style={styles.resendContainer}>
            <Text style={styles.resendText}>{t('auth.didntReceiveOtp')}</Text>
            <Clickable2 onPress={resendOTP} accessibilityLabel={t('auth.resend')} accessibilityRole="button">
              <Text style={styles.resendLink}>{t('auth.resend')}</Text>
            </Clickable2>
          </View>
        ) : (
          <View style={styles.timerContainer}>
            <Text style={styles.timerText}>
              {t('auth.resendAfter')}
              <Text style={styles.timerValue}>00:{timer.toString().padStart(2, '0')}</Text>
              {t('auth.sec')}
            </Text>
          </View>
        )}

        <View style={{ marginTop: authSheetFormSpacing.beforePrimaryActions, alignSelf: 'stretch', width: '100%' }}>
          <View style={sharedStyles.buttonStack}>
            <Clickable2
              onPress={verifyOTP}
              disabled={!isOtpComplete || isLoading}
              style={[
                { alignSelf: 'stretch', flexShrink: 0 },
                !isOtpComplete && !isLoading ? { opacity: 0.5 } : null,
              ]}
              accessibilityLabel={t('auth.verify')}
              accessibilityRole="button"
              accessibilityState={{ disabled: !isOtpComplete || isLoading }}
            >
              <View style={[buttonWrap, { shadowColor: colors.shadowColor }]}>
                <View style={frosted_primary.surface}>
                  {isLoading ? (
                    <ButtonLoader size={16} color={colors.onPrimary ?? colors.primaryText ?? colors.white} />
                  ) : (
                    <Text style={frosted_primary.label}>{t('auth.verify')}</Text>
                  )}
                </View>
              </View>
            </Clickable2>
          </View>
        </View>
        {show_blocking_abort ? (
          <Clickable2
            onPress={() => confirmBlockingOtpAbort(t, onAbortSafeExit!)}
            accessibilityLabel={t('common.cancel')}
            accessibilityRole="button"
            style={{ alignSelf: 'center', marginTop: authSheetFormSpacing.beforePrimaryActions, paddingVertical: appTheme.spacing.sm }}>
            <Text style={styles.resendLink}>{t('common.cancel')}</Text>
          </Clickable2>
        ) : null}
      </ScrollView>
    </View>
  );
}

export default OTPVerificationSheetContent;
